import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";
import { Card } from "@/shared/components/ui/card";

/// A read card shows the first rows only; the page it links to is where the full list lives.
export const READ_CARD_ROW_LIMIT = 5;

/// Chrome shared by every Hand read card: a quiet title, the rows, and a way to the real page
/// when one exists that shows the same data.
export function ReadCardFrame({
  title,
  note,
  viewAllHref,
  hiddenCount,
  hasMore = false,
  children,
}: {
  title: string;
  /** Small context beside the title, such as the scale a score is read on. */
  note?: string;
  viewAllHref?: string;
  hiddenCount: number;
  /** The server has further pages, so any count of what is left out would be too low. */
  hasMore?: boolean;
  children: ReactNode;
}) {
  return (
    <Card className="my-2 gap-0 overflow-hidden py-0 text-sm shadow-none">
      <section aria-label={title}>
        <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
          <h3 className="min-w-0 truncate text-xs font-medium text-text-secondary">
            {title}
            {note ? <span className="ml-1.5 font-normal text-text-tertiary">{note}</span> : null}
          </h3>
          {viewAllHref ? (
            <a
              href={viewAllHref}
              className="inline-flex shrink-0 items-center gap-0.5 rounded text-xs text-primary hover:underline focus-visible:outline-2 focus-visible:outline-ring"
            >
              View all
              <ArrowUpRight aria-hidden="true" className="size-3" />
            </a>
          ) : null}
        </header>
        <ul className="divide-y divide-border">{children}</ul>
        {hasMore || hiddenCount > 0 ? (
          <p className="border-t border-border px-3 py-1.5 text-xs text-text-tertiary">
            {hasMore ? (
              "More on the full page"
            ) : viewAllHref ? (
              <a
                aria-label={`+${hiddenCount} more, open the full list`}
                href={viewAllHref}
                className="inline-flex items-center gap-0.5 rounded text-primary hover:underline focus-visible:outline-2 focus-visible:outline-ring"
              >
                {`+${hiddenCount} more`}
              </a>
            ) : (
              `+${hiddenCount} more`
            )}
          </p>
        ) : null}
      </section>
    </Card>
  );
}
